// Undo and redo for Watchlist draft edits.
//
// Every user action (one editor commit, clearing a selection, typing into a
// selection, a fill, a per-cell undo) is one history entry, so Cmd/Ctrl+Z
// reverses a 400-cell fill in one step. Entries store the draft before and
// after each affected cell; `null` means "no draft", i.e. the saved value.
// The same history also holds the owner's other draft steps in order (a
// security picked in the "+ Add security" row: see pendingAdditions.js),
// which carry no `changes` and are applied by the owner.

export const EMPTY_DRAFT_HISTORY = Object.freeze({ past: [], future: [] });
export const MAX_DRAFT_HISTORY_ACTIONS = 100;

function sameDraft(left, right) {
    if (left === right) return true;
    if (!left || !right) return false;
    return left.value === right.value
        && left.cogniveId === right.cogniveId
        && left.attributeDefinitionId === right.attributeDefinitionId;
}

/**
 * Turn requested cell values into one history action against the current
 * drafts. A value equal to the saved baseline removes the draft, matching the
 * single-cell editor. Cells that would not change are left out; the result is
 * null when nothing changes.
 *
 * `edits`: [{ key, cogniveId, attributeDefinitionId, value, baselineValue }]
 * with `value` and `baselineValue` already normalized to trimmed text.
 */
export function buildDraftAction(drafts, edits, label) {
    const changes = [];
    const seen = new Set();
    edits.forEach((edit) => {
        if (seen.has(edit.key)) return;
        seen.add(edit.key);
        const before = Object.prototype.hasOwnProperty.call(drafts, edit.key)
            ? drafts[edit.key]
            : null;
        const after = edit.value === edit.baselineValue
            ? null
            : {
                cogniveId: edit.cogniveId,
                attributeDefinitionId: edit.attributeDefinitionId,
                value: edit.value,
            };
        if (!sameDraft(before, after)) changes.push({ key: edit.key, before, after });
    });
    return changes.length ? { label, changes } : null;
}

/** Apply one side ('before' or 'after') of an action's changes to the drafts. */
export function applyDraftChanges(drafts, changes, side) {
    const next = { ...drafts };
    changes.forEach((change) => {
        const draft = change[side];
        if (draft) {
            next[change.key] = draft;
        } else {
            delete next[change.key];
        }
    });
    return next;
}

export function recordDraftAction(history, action) {
    if (!action || action.changes?.length === 0) return history;
    return {
        past: [...history.past, action].slice(-MAX_DRAFT_HISTORY_ACTIONS),
        future: [],
    };
}

/** The history with only the actions `keep` accepts, in order (e.g. after one kind of draft was saved). */
export function filterDraftHistory(history, keep) {
    const past = history.past.filter(keep);
    const future = history.future.filter(keep);
    return past.length === history.past.length && future.length === history.future.length
        ? history
        : { past, future };
}

/** The action to reverse and the history after it, or null when empty. */
export function takeUndo(history) {
    if (history.past.length === 0) return null;
    const action = history.past[history.past.length - 1];
    return {
        action,
        history: {
            past: history.past.slice(0, -1),
            future: [action, ...history.future],
        },
    };
}

/** The action to re-apply and the history after it, or null when empty. */
export function takeRedo(history) {
    if (history.future.length === 0) return null;
    const [action, ...future] = history.future;
    return {
        action,
        history: {
            past: [...history.past, action],
            future,
        },
    };
}
